import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { searchCompanies, updateCompany, deleteCompany, getCategories, getDuplicateGroupMembers } from '../services/companyService'
import { checkIfEmpty, importFromCSV } from '../services/importService'
import {
  Company,
  CompanyFilters,
  PaginatedResult,
  DEFAULT_FILTERS,
  TABLE_COLUMNS,
  FIELD_GROUPS,
  CONFIDENCE_COLORS,
} from '../types'

const FONT = 'Lexend Deca, sans-serif'

export default function AllCompaniesDashboard() {
  const [result, setResult] = useState<PaginatedResult<Company>>({ data: [], total: 0, page: 1, pageSize: 50, totalPages: 0 })
  const [filters, setFilters] = useState<CompanyFilters>(DEFAULT_FILTERS)
  const [categories, setCategories] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [isEmpty, setIsEmpty] = useState(false)
  const [importing, setImporting] = useState(false)
  const [importProgress, setImportProgress] = useState('')
  const [toast, setToast] = useState<string | null>(null)
  const toastTimer = useRef<NodeJS.Timeout | null>(null)

  // Detail panel
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null)
  const [editValues, setEditValues] = useState<Record<string, string>>({})
  const [detailMode, setDetailMode] = useState<'view' | 'edit'>('view')
  const [saving, setSaving] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(false)

  // Duplicate group panel
  const [dupGroupMembers, setDupGroupMembers] = useState<Company[]>([])
  const [showDupGroup, setShowDupGroup] = useState(false)

  const searchInputRef = useRef<HTMLInputElement>(null)
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null)

  const showToast = useCallback((msg: string) => {
    setToast(msg)
    if (toastTimer.current) clearTimeout(toastTimer.current)
    toastTimer.current = setTimeout(() => setToast(null), 2500)
  }, [])

  // Check if table is empty on mount
  useEffect(() => {
    const init = async () => {
      try {
        const empty = await checkIfEmpty()
        if (empty) {
          setIsEmpty(true)
          setLoading(false)
          return
        }
        const cats = await getCategories()
        setCategories(cats)
        await fetchData(DEFAULT_FILTERS)
      } catch (err) {
        console.error('Init error:', err)
      } finally {
        setLoading(false)
      }
    }
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchData = useCallback(async (f: CompanyFilters) => {
    setLoading(true)
    try {
      const res = await searchCompanies(f)
      setResult(res)
    } catch (err) {
      console.error('Fetch error:', err)
      showToast('Failed to load companies')
    } finally {
      setLoading(false)
    }
  }, [showToast])

  const handleFilterChange = useCallback((partial: Partial<CompanyFilters>) => {
    setFilters(prev => {
      const next = { ...prev, ...partial, page: partial.page !== undefined ? partial.page : 1 }
      fetchData(next)
      return next
    })
  }, [fetchData])

  const handleSearchInput = useCallback((value: string) => {
    setFilters(prev => ({ ...prev, search: value }))
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    searchTimerRef.current = setTimeout(() => {
      handleFilterChange({ search: value })
    }, 300)
  }, [handleFilterChange])

  const handleSort = useCallback((field: string) => {
    setFilters(prev => {
      const newDir = prev.sortField === field && prev.sortDir === 'asc' ? 'desc' : 'asc'
      const next = { ...prev, sortField: field, sortDir: newDir as 'asc' | 'desc', page: 1 }
      fetchData(next)
      return next
    })
  }, [fetchData])

  // Import handler
  const handleImport = useCallback(async () => {
    setImporting(true)
    try {
      const result = await importFromCSV((p) => {
        setImportProgress(`${p.phase} ${p.total ? `(${p.current.toLocaleString()} / ${p.total.toLocaleString()})` : ''}`)
      })
      showToast(`Imported ${result.imported.toLocaleString()} companies (${result.duplicatesTagged} duplicates tagged)`)
      setIsEmpty(false)
      const cats = await getCategories()
      setCategories(cats)
      await fetchData(DEFAULT_FILTERS)
    } catch (err: any) {
      console.error('Import failed:', err)
      showToast(`Import failed: ${err.message}`)
    } finally {
      setImporting(false)
      setImportProgress('')
    }
  }, [fetchData, showToast])

  // Detail panel handlers
  const openDetail = useCallback(async (company: Company) => {
    setSelectedCompany(company)
    setEditValues({})
    setDetailMode('view')
    setDeleteConfirm(false)
    setShowDupGroup(false)
    setDupGroupMembers([])
  }, [])

  const startEdit = useCallback(() => {
    if (!selectedCompany) return
    const vals: Record<string, string> = {}
    for (const group of FIELD_GROUPS) {
      for (const f of group.fields) {
        vals[f.key] = (selectedCompany as any)[f.key] || ''
      }
    }
    setEditValues(vals)
    setDetailMode('edit')
  }, [selectedCompany])

  const handleSave = useCallback(async () => {
    setSaving(true)
    try {
      if (detailMode === 'edit' && selectedCompany) {
        const updated = await updateCompany(selectedCompany.id, editValues)
        setSelectedCompany(updated)
        showToast('Company updated')
      }
      setDetailMode('view')
      fetchData(filters)
    } catch (err: any) {
      showToast(err.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }, [detailMode, editValues, selectedCompany, filters, fetchData, showToast])

  const handleDelete = useCallback(async () => {
    if (!selectedCompany) return
    try {
      await deleteCompany(selectedCompany.id)
      showToast('Company deleted')
      setSelectedCompany(null)
      setDetailMode('view')
      fetchData(filters)
    } catch (err: any) {
      showToast(err.message || 'Delete failed')
    }
  }, [selectedCompany, filters, fetchData, showToast])

  const loadDupGroup = useCallback(async (groupId: string) => {
    const members = await getDuplicateGroupMembers(groupId)
    setDupGroupMembers(members)
    setShowDupGroup(true)
  }, [])

  const closeDetail = useCallback(() => {
    setSelectedCompany(null)
    setDetailMode('view')
    setDeleteConfirm(false)
    setShowDupGroup(false)
  }, [])

  // Page navigation
  const goToPage = useCallback((p: number) => {
    handleFilterChange({ page: p })
  }, [handleFilterChange])

  const pageNumbers = useMemo(() => {
    const pages: number[] = []
    const total = result.totalPages
    const current = filters.page
    const delta = 2
    const start = Math.max(1, current - delta)
    const end = Math.min(total, current + delta)
    if (start > 1) pages.push(1)
    if (start > 2) pages.push(-1) // ellipsis
    for (let i = start; i <= end; i++) pages.push(i)
    if (end < total - 1) pages.push(-1)
    if (end < total) pages.push(total)
    return pages
  }, [result.totalPages, filters.page])

  // ─── Empty / Import state ──────────────────────────────────
  if (isEmpty && !importing) {
    return (
      <div style={{ fontFamily: FONT, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '16px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#333' }}>No company data found</h2>
        <p style={{ fontSize: '14px', color: '#666', maxWidth: '400px', textAlign: 'center' }}>
          Import the Companies.csv data to get started. This will load all company records and tag any identified duplicates.
        </p>
        <button onClick={handleImport} style={{ ...primaryBtnStyle, padding: '12px 32px', fontSize: '15px' }}>
          Import from CSV
        </button>
      </div>
    )
  }

  if (importing) {
    return (
      <div style={{ fontFamily: FONT, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '16px' }}>
        <div style={{ width: '40px', height: '40px', border: '3px solid #e0e0e0', borderTopColor: '#1a73e8', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <p style={{ fontSize: '14px', color: '#666' }}>{importProgress || 'Starting import...'}</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    )
  }

  // ─── Main view ─────────────────────────────────────────────
  return (
    <div style={{ fontFamily: FONT, display: 'flex', flexDirection: 'column', height: '100vh', background: '#f8f9fa' }}>
      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 1000, background: '#333', color: '#fff', padding: '10px 20px', borderRadius: '8px', fontSize: '13px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          {toast}
        </div>
      )}

      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e0e0e0', padding: '16px 24px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: '20px', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>All Companies</h1>
        <span style={{ fontSize: '13px', color: '#888', fontWeight: 500 }}>
          {result.total.toLocaleString()} records
        </span>
        <div style={{ marginLeft: 'auto' }} />
      </div>

      {/* Filters */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e0e0e0', padding: '10px 24px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search name, email, phone, website, reg no..."
          value={filters.search}
          onChange={e => handleSearchInput(e.target.value)}
          style={{ padding: '7px 12px', border: '1px solid #d0d0d0', borderRadius: '6px', fontSize: '13px', fontFamily: FONT, width: '320px', outline: 'none' }}
        />
        <select
          value={filters.category}
          onChange={e => handleFilterChange({ category: e.target.value })}
          style={selectStyle}
        >
          <option value="">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          value={filters.duplicateStatus}
          onChange={e => handleFilterChange({ duplicateStatus: e.target.value as any })}
          style={selectStyle}
        >
          <option value="all">All Records</option>
          <option value="duplicates">Duplicates Only</option>
          <option value="no_duplicates">No Duplicates</option>
        </select>
        {(filters.search || filters.category || filters.duplicateStatus !== 'all') && (
          <button
            onClick={() => {
              setFilters(DEFAULT_FILTERS)
              fetchData(DEFAULT_FILTERS)
            }}
            style={{ padding: '7px 14px', border: '1px solid #d0d0d0', borderRadius: '6px', background: '#fff', fontSize: '12px', cursor: 'pointer', fontFamily: FONT, color: '#666' }}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Table + Detail split */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Table */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f1f3f4', position: 'sticky', top: 0, zIndex: 2 }}>
                <th style={{ ...thStyle, width: '36px' }}></th>
                {TABLE_COLUMNS.map(col => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    style={{ ...thStyle, width: col.width, cursor: 'pointer', userSelect: 'none' }}
                  >
                    {col.label}
                    {filters.sortField === col.key && (
                      <span style={{ marginLeft: '4px', fontSize: '10px' }}>
                        {filters.sortDir === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && result.data.length === 0 ? (
                <tr><td colSpan={TABLE_COLUMNS.length + 1} style={{ padding: '40px', textAlign: 'center', color: '#888' }}>Loading...</td></tr>
              ) : result.data.length === 0 ? (
                <tr><td colSpan={TABLE_COLUMNS.length + 1} style={{ padding: '40px', textAlign: 'center', color: '#888' }}>No companies found</td></tr>
              ) : (
                result.data.map(company => {
                  const isSelected = selectedCompany?.id === company.id
                  const hasDup = !!company.duplicate_group_id
                  return (
                    <tr
                      key={company.id}
                      onClick={() => openDetail(company)}
                      style={{
                        cursor: 'pointer',
                        background: isSelected ? '#e8f0fe' : '#fff',
                        borderBottom: '1px solid #eee',
                        borderLeft: hasDup ? '3px solid ' + (CONFIDENCE_COLORS[company.duplicate_confidence || '']?.text || '#999') : '3px solid transparent',
                      }}
                      onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = '#f8f9fa' }}
                      onMouseLeave={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = '#fff' }}
                    >
                      <td style={tdStyle}>
                        {hasDup && (
                          <DuplicateBadge confidence={company.duplicate_confidence || ''} />
                        )}
                      </td>
                      {TABLE_COLUMNS.map(col => (
                        <td key={col.key} style={{ ...tdStyle, maxWidth: col.width, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {(company as any)[col.key] || ''}
                        </td>
                      ))}
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {result.totalPages > 1 && (
            <div style={{ padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', borderTop: '1px solid #eee', background: '#fff' }}>
              <button
                onClick={() => goToPage(filters.page - 1)}
                disabled={filters.page <= 1}
                style={pageBtnStyle}
              >
                Prev
              </button>
              {pageNumbers.map((p, i) =>
                p === -1 ? (
                  <span key={`e${i}`} style={{ padding: '0 4px', color: '#999' }}>...</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => goToPage(p)}
                    style={{
                      ...pageBtnStyle,
                      background: p === filters.page ? '#1a73e8' : '#fff',
                      color: p === filters.page ? '#fff' : '#333',
                      fontWeight: p === filters.page ? 600 : 400,
                    }}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                onClick={() => goToPage(filters.page + 1)}
                disabled={filters.page >= result.totalPages}
                style={pageBtnStyle}
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Detail panel */}
        {selectedCompany && (
          <div style={{ width: '420px', borderLeft: '1px solid #e0e0e0', background: '#fff', overflow: 'auto', flexShrink: 0 }}>
            <DetailPanel
              company={selectedCompany}
              mode={detailMode}
              editValues={editValues}
              setEditValues={setEditValues}
              saving={saving}
              deleteConfirm={deleteConfirm}
              setDeleteConfirm={setDeleteConfirm}
              onClose={closeDetail}
              onEdit={startEdit}
              onSave={handleSave}
              onDelete={handleDelete}

              dupGroupMembers={dupGroupMembers}
              showDupGroup={showDupGroup}
              onLoadDupGroup={loadDupGroup}
              onSelectCompany={openDetail}
            />
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Sub-components ────────────────────────────────────────

function DuplicateBadge({ confidence }: { confidence: string }) {
  const colors = CONFIDENCE_COLORS[confidence] || { bg: '#eee', text: '#666' }
  return (
    <span style={{
      display: 'inline-block', padding: '2px 6px', borderRadius: '4px', fontSize: '10px',
      fontWeight: 600, background: colors.bg, color: colors.text, textTransform: 'capitalize',
      whiteSpace: 'nowrap',
    }}>
      {confidence || 'dup'}
    </span>
  )
}

interface DetailPanelProps {
  company: Company | null
  mode: 'view' | 'edit'
  editValues: Record<string, string>
  setEditValues: React.Dispatch<React.SetStateAction<Record<string, string>>>
  saving: boolean
  deleteConfirm: boolean
  setDeleteConfirm: (v: boolean) => void
  onClose: () => void
  onEdit: () => void
  onSave: () => void
  onDelete: () => void

  dupGroupMembers: Company[]
  showDupGroup: boolean
  onLoadDupGroup: (groupId: string) => void
  onSelectCompany: (c: Company) => void
}

function DetailPanel({
  company, mode, editValues, setEditValues, saving, deleteConfirm, setDeleteConfirm,
  onClose, onEdit, onSave, onDelete, dupGroupMembers, showDupGroup, onLoadDupGroup, onSelectCompany,
}: DetailPanelProps) {
  const isEditing = mode === 'edit'

  const handleFieldChange = (key: string, value: string) => {
    setEditValues(prev => ({ ...prev, [key]: value }))
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Panel header */}
      <div style={{ padding: '14px 16px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <h3 style={{ fontSize: '15px', fontWeight: 600, margin: 0, flex: 1, fontFamily: FONT }}>
          {company?.company_name || 'Company Detail'}
        </h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#999', padding: '4px' }}>×</button>
      </div>

      {/* Duplicate info */}
      {company?.duplicate_group_id && mode === 'view' && (
        <div style={{ padding: '10px 16px', background: '#fffbeb', borderBottom: '1px solid #fde68a', fontSize: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <DuplicateBadge confidence={company.duplicate_confidence || ''} />
            <span style={{ color: '#92400e' }}>
              Duplicate group <strong>{company.duplicate_group_id}</strong>
            </span>
            <button
              onClick={() => onLoadDupGroup(company.duplicate_group_id!)}
              style={{ marginLeft: 'auto', fontSize: '11px', color: '#1a73e8', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontFamily: FONT }}
            >
              {showDupGroup ? 'Hide group' : 'View group'}
            </button>
          </div>
          {company.duplicate_match_reasons && (
            <div style={{ marginTop: '4px', color: '#78350f', fontSize: '11px' }}>
              Match: {company.duplicate_match_reasons}
            </div>
          )}
          {showDupGroup && dupGroupMembers.length > 0 && (
            <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {dupGroupMembers.map(m => (
                <div
                  key={m.id}
                  onClick={() => onSelectCompany(m)}
                  style={{
                    padding: '6px 8px', background: m.id === company.id ? '#fef3c7' : '#fff',
                    borderRadius: '4px', cursor: 'pointer', fontSize: '12px',
                    border: m.id === company.id ? '1px solid #f59e0b' : '1px solid #e5e7eb',
                  }}
                >
                  <strong>{m.company_name || '(no name)'}</strong>
                  {m.email_1 && <span style={{ color: '#666', marginLeft: '8px' }}>{m.email_1}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Fields */}
      <div style={{ flex: 1, overflow: 'auto', padding: '8px 0' }}>
        {FIELD_GROUPS.map(group => (
          <div key={group.label}>
            <div style={{ padding: '8px 16px 4px', fontSize: '11px', fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {group.label}
            </div>
            {group.fields.map(f => {
              const value = isEditing
                ? (editValues[f.key] ?? '')
                : ((company as any)?.[f.key] || '')
              return (
                <div key={f.key} style={{ padding: '4px 16px', display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px' }}>
                  <label style={{ width: '110px', flexShrink: 0, color: '#666', paddingTop: '4px', fontSize: '12px' }}>
                    {f.label}
                  </label>
                  {isEditing ? (
                    f.multiline ? (
                      <textarea
                        value={value}
                        onChange={e => handleFieldChange(f.key, e.target.value)}
                        rows={4}
                        style={{ flex: 1, padding: '4px 8px', border: '1px solid #d0d0d0', borderRadius: '4px', fontSize: '13px', fontFamily: FONT, resize: 'vertical' }}
                      />
                    ) : (
                      <input
                        type="text"
                        value={value}
                        onChange={e => handleFieldChange(f.key, e.target.value)}
                        style={{ flex: 1, padding: '4px 8px', border: '1px solid #d0d0d0', borderRadius: '4px', fontSize: '13px', fontFamily: FONT }}
                      />
                    )
                  ) : (
                    <span style={{ flex: 1, color: value ? '#333' : '#ccc', wordBreak: 'break-word', paddingTop: '4px' }}>
                      {value || '—'}
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        ))}
      </div>

      {/* Actions */}
      <div style={{ padding: '12px 16px', borderTop: '1px solid #eee', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {isEditing ? (
          <>
            <button onClick={onSave} disabled={saving} style={primaryBtnStyle}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={onClose} style={secondaryBtnStyle}>Cancel</button>
          </>
        ) : (
          <>
            <button onClick={onEdit} style={primaryBtnStyle}>Edit</button>
            {!deleteConfirm ? (
              <button onClick={() => setDeleteConfirm(true)} style={dangerBtnStyle}>Delete</button>
            ) : (
              <>
                <button onClick={onDelete} style={{ ...dangerBtnStyle, background: '#dc2626', color: '#fff' }}>Confirm Delete</button>
                <button onClick={() => setDeleteConfirm(false)} style={secondaryBtnStyle}>Cancel</button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ─── Styles ───────────────────────────────────────────────

const thStyle: React.CSSProperties = {
  padding: '10px 12px', textAlign: 'left', fontSize: '12px', fontWeight: 600,
  color: '#555', borderBottom: '2px solid #e0e0e0', fontFamily: FONT, whiteSpace: 'nowrap',
}

const tdStyle: React.CSSProperties = {
  padding: '8px 12px', fontSize: '13px', color: '#333', fontFamily: FONT,
}

const selectStyle: React.CSSProperties = {
  padding: '7px 10px', border: '1px solid #d0d0d0', borderRadius: '6px',
  fontSize: '13px', fontFamily: FONT, background: '#fff', cursor: 'pointer',
}

const primaryBtnStyle: React.CSSProperties = {
  padding: '7px 18px', border: 'none', borderRadius: '6px', background: '#1a73e8',
  color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: FONT,
}

const secondaryBtnStyle: React.CSSProperties = {
  padding: '7px 18px', border: '1px solid #d0d0d0', borderRadius: '6px', background: '#fff',
  color: '#333', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: FONT,
}

const dangerBtnStyle: React.CSSProperties = {
  padding: '7px 18px', border: '1px solid #dc2626', borderRadius: '6px', background: '#fff',
  color: '#dc2626', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: FONT,
}

const pageBtnStyle: React.CSSProperties = {
  padding: '5px 10px', border: '1px solid #d0d0d0', borderRadius: '4px', background: '#fff',
  color: '#333', fontSize: '12px', cursor: 'pointer', fontFamily: FONT, minWidth: '32px',
}
